import { createTheme, Menu, MenuItem, ThemeProvider } from "@mui/material";
import React, { useRef, useState } from "react";
import styled, { css } from "styled-components/macro";
import {
  lightAccentInfo20,
  lightAccentInfo50,
  lightTextSubheading,
  summarySectionBottomBackground,
  summarySectionBottomBorder,
} from "../utils/tier0-colors";
import { formatThousandsWithComma } from "../utils/tier0-utils";
import { useMeasure } from "react-use";
import { BREAKPOINT_DESKTOP_XL } from "constants/constants";
import ErrorBoundary from "components/SimpleComponents/Errors/ErrorBoundary";
import { useWorkScheduleConfigJsonListAtom } from "store/atoms";
import { useLocation, useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { clearAllAggregates } from "providers/GlobalStateProvider";
import { useAtom } from "jotai";
import { prevShiftDDAAtom, useProcessIdsMapOnTier0Page } from "../tier0Utils";
const FIXED_ITEM_WIDTH = 264;

export type SummaryBoxProps = {
  dateReadable: string;
  shiftName: string;
  lineName: string;
  supervisorName: string;
  leadPersonName: string;
  prevShiftHeadcount: number;
  prevShiftNumCompletedOrders: number;
  prevShiftTotalUnitsProduced: number;
  prevShiftLoading: boolean;
};
const SummaryBox = ({
  dateReadable,
  shiftName,
  lineName,
  supervisorName,
  leadPersonName,
  prevShiftHeadcount,
  prevShiftNumCompletedOrders,
  prevShiftTotalUnitsProduced,
  prevShiftLoading,
}: SummaryBoxProps): React.JSX.Element => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const processGroups = useProcessIdsMapOnTier0Page();
  const [measureRef, { width: refWidth }] = useMeasure<HTMLDivElement>();
  const scale = Math.max(1, refWidth / BREAKPOINT_DESKTOP_XL);
  const { search } = useLocation();
  const navigate = useNavigate();
  const [workScheduleConfigList] = useWorkScheduleConfigJsonListAtom();
  const dispatch = useDispatch();
  const [, setPrevShiftDDA] = useAtom(prevShiftDDAAtom);
  const hideNav = search.includes("hideNav=true");

  return (
    <Styles scale={scale} ref={measureRef}>
      <div className="title-left">
        <div className="section above">
          <h3>General Information</h3>
          <h3>Informacion General</h3>
        </div>
        <div className="section below">
          <h3>Previous Shift</h3>
          <h3>Turnos Anteriores</h3>
        </div>
      </div>
      <div className="content-right">
        <div className="content-top">
          <ContentItem
            className="grow"
            textTop={"Date | Fecha"}
            textBottom={dateReadable ? dateReadable : "--"}
          />
          <ContentItem
            className="fixedWidth"
            textTop={"Shift | Cambio"}
            textBottom={shiftName ? shiftName : "--"}
          />
          <ContentItem
            ref={ref}
            onClick={() => {
              if (hideNav) {
                return;
              }
              setOpen(true);
            }}
            className="grow"
            textTop={"Line | Línea"}
            textBottom={lineName ? lineName : "--"}
            customCss={css`
              &:hover {
                background: #00000013;
              }
              padding-top: 8px;
              margin-top: -8px;
              padding-bottom: 8px;
              margin-bottom: -8px;
            `}
          />
          {processGroups ? (
            <Menu
              open={open}
              onClose={() => setOpen(false)}
              anchorReference="anchorPosition"
              anchorPosition={
                open && ref.current
                  ? {
                      top: ref.current.getBoundingClientRect().top,
                      left: ref.current.getBoundingClientRect().left,
                    }
                  : undefined
              }
            >
              {Object.entries(processGroups).map(
                ([groupName, processesObj]) => {
                  return [
                    <MenuItem
                      key={groupName}
                      style={{
                        fontWeight: "bold",
                        pointerEvents: "none",
                        background: "#eaeaea",
                      }}
                    >
                      {groupName}
                    </MenuItem>,
                    Object.entries(processesObj).map(
                      ([_machemetryId, processName]) => (
                        <MenuItem
                          key={processName}
                          style={
                            processName === lineName
                              ? {
                                  pointerEvents: "none",
                                  background: lightAccentInfo20,
                                }
                              : {}
                          }
                          onClick={() => {
                            // setSelectedProcessName?.(processName);
                            setOpen(false);

                            // clear dataDumpFullRange + trendChartBars DDAs (tier0 hour rows) (otherwise we can get duplicate rows)
                            dispatch(clearAllAggregates());
                            setPrevShiftDDA(null);

                            // when we change selectedProcessName, set it in the url search params as &process=<processName> + workSchedule=<workScheduleConfig.id>
                            const urlSearchParams = new URLSearchParams(search);

                            const selectedProcessWorkSchedule =
                              workScheduleConfigList.find(ws =>
                                ws.processes.find(p =>
                                  p.taps.find(t => t.name === processName)
                                )
                              )?.id;

                            urlSearchParams.set(
                              "workSchedule",
                              selectedProcessWorkSchedule ?? ""
                            );
                            urlSearchParams.set("process", processName);
                            navigate(
                              `${
                                window.location.pathname
                              }?${urlSearchParams.toString()}`
                            );
                          }}
                        >
                          {processName}
                        </MenuItem>
                      )
                    ),
                  ];
                }
              )}
            </Menu>
          ) : null}

          {/* hidden until we know where this data comes from */}
          {/* <ContentItem className="fixedWidth" textTop={"Supervisor"} textBottom={supervisorName} /> */}
          {/* <ContentItem className="fixedWidth" textTop={"Lead | Líder"} textBottom={leadPersonName} /> */}
        </div>
        <div className="content-bottom">
          <ErrorBoundary>
            <ContentItem
              className="grow"
              textTop={"Total Units Produced | Unidad Total"}
              textBottom={
                prevShiftLoading
                  ? "..."
                  : prevShiftTotalUnitsProduced
                    ? formatThousandsWithComma(prevShiftTotalUnitsProduced)
                    : "--"
              }
            />
          </ErrorBoundary>
          <ContentItem
            className="fixedWidth"
            textTop={"Headcount | Empleados"}
            textBottom={`${
              prevShiftLoading
                ? "..."
                : prevShiftHeadcount
                  ? prevShiftHeadcount
                  : "--"
            }`}
          />
          <ContentItem
            className="grow"
            textTop={"Completed SKUs"}
            textBottom={`${
              prevShiftLoading
                ? "..."
                : prevShiftNumCompletedOrders
                  ? prevShiftNumCompletedOrders
                  : "--"
            } SKU${prevShiftNumCompletedOrders === 1 ? "" : "s"}`}
          />
        </div>
      </div>
    </Styles>
  );
};

const theme = createTheme();
function SummaryBoxWithMuiThemeProvider(props: SummaryBoxProps) {
  return (
    <ThemeProvider theme={theme}>
      <SummaryBox {...props} />
    </ThemeProvider>
  );
}
export default SummaryBoxWithMuiThemeProvider;

const ContentItem = React.forwardRef<
  any,
  {
    textTop: string;
    textBottom: string;
    className?: string;
    onClick?: () => void;
    customCss?: string;
  }
>(({ textTop, textBottom, className, onClick, customCss }, ref) => {
  return (
    <ContentItemStyles
      ref={ref}
      className={`content-item ${className ?? ""}`}
      onClick={onClick}
      $customCss={customCss}
    >
      <div className="top">{textTop}</div>
      <div className="bottom bold">{textBottom}</div>
    </ContentItemStyles>
  );
});
const ContentItemStyles = styled.div<{ $customCss?: string }>`
  ${props =>
    props.onClick
      ? css`
          cursor: pointer;
        `
      : ""}
  ${props => props.$customCss}
`;
const Styles = styled.div<{ scale: number }>`
  display: flex;
  background: white;
  padding-left: ${p => p.scale * 20}px;
  * {
    font-family: "Open Sans", sans-serif;
  }
  h3 {
    margin: 0;
    font-weight: 700;
    font-size: ${p => p.scale * 14}px;
    line-height: ${p => p.scale * 20}px;
    color: ${lightTextSubheading};
  }
  .content-item {
    position: relative;
    padding-left: ${p => p.scale * 32}px;
    &:not(:last-child):after {
      content: "";
      position: absolute;
      top: ${p => p.scale * 5}px;
      bottom: ${p => p.scale * 5}px;
      right: 0;
      width: ${p => p.scale * 1}px;
    }
    .top,
    .bottom {
      line-height: ${p => p.scale * 24}px;
    }
  }
  .content-top .content-item:after {
    background: ${lightAccentInfo50};
  }
  .content-bottom .content-item:after {
    background: ${summarySectionBottomBorder};
  }
  .title-left {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding-right: ${p => p.scale * 20}px;
  }
  .above {
  }

  .below {
  }
  .fixedWidth {
    width: ${p => p.scale * FIXED_ITEM_WIDTH}px;
  }
  .grow {
    width: calc(50% - ${p => (p.scale * FIXED_ITEM_WIDTH) / 2}px);
  }
  .content-right {
    flex-grow: 1;
  }
  .content-top {
    display: flex;
    border: ${p => p.scale * 1}px solid ${lightAccentInfo50};
    background: ${lightAccentInfo20};
    border-radius: ${p => p.scale * 12}px ${p => p.scale * 8}px 0px 0px;
  }
  .content-bottom {
    display: flex;
    border: ${p => p.scale * 1}px solid ${summarySectionBottomBorder};
    background: ${summarySectionBottomBackground};
    border-radius: 0px 0px ${p => p.scale * 8}px ${p => p.scale * 12}px;
  }
  .section {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: start;
  }
  .section,
  .content-top,
  .content-bottom {
    height: ${p => p.scale * 62}px;
  }
  .content-top,
  .content-bottom {
    align-items: center;
  }
  .top {
    font-size: ${p => p.scale * 16}px;
  }
  .bold {
    font-weight: bold;
    font-size: ${p => p.scale * 24}px;
  }
`;
